import type { McpServerField, NeedsField, OauthConnectionField } from '@developerz/domain';
import { IconCheck } from '@developerz/ui';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { integrationOauthLoginUrl } from '../lib/integrations';
import { StatusPill } from './StatusPill';
import { buttonClass } from './ui/Button';

// ONE field of a v2 needs form (`entities/blocker/needs-form.ts`), by kind, and
// whether it has landed. Presenter — `<BlockerNeeds/>` decides `landed` from the
// row's `landed` NAMES.
//
// WHAT IS ON SCREEN IS THE ASK, NEVER THE ANSWER. Every string here comes from
// the field SPEC the raiser wrote (a name, an environment, where an MCP server
// lives, the NAME of the credential it authenticates with), and `landed` is a
// list of names. A supplied value went to its store and never came back to this
// row, so there is no value to render — and no input here to type one into:
// values go through the one-link form, which is write-only.
//
// `oauth_connection` IS THE ONE FIELD THIS CARD CAN HELP FINISH. The one-link
// form cannot complete an OAuth dance (it holds no dashboard session), so an
// outstanding one offers Connect: a real anchor onto the existing login leg,
// same tab, because the leg's tx cookie must be first-party (`lib/integrations.ts`).

const vault = (f: NeedsField) =>
  f.kind === 'env_secret' || f.kind === 'service_url' ? f : undefined;
const mcp = (f: NeedsField): McpServerField | undefined =>
  f.kind === 'mcp_server' ? f : undefined;
const oauth = (f: NeedsField): OauthConnectionField | undefined =>
  f.kind === 'oauth_connection' ? f : undefined;

/** The field's name as a human reads it: the provider's display name for OAuth, else its own name. */
function fieldTitle(f: NeedsField): string {
  return f.kind === 'oauth_connection' ? t(`integrations.kind.${f.provider}`) : f.name;
}

export function BlockerNeedsField(props: {
  readonly field: NeedsField;
  readonly landed: boolean;
}): JSX.Element {
  return (
    <li class="blocker-needs__field">
      <div class="blocker-needs__head">
        <span class="badge badge--outline">{t(`blockers.field_kind_${props.field.kind}`)}</span>
        <span class="blocker-needs__name">{fieldTitle(props.field)}</span>
        <StatusPill tone={props.landed ? 'success' : 'warning'}>
          <Show when={props.landed}>
            <IconCheck />
          </Show>
          {t(props.landed ? 'blockers.field_landed' : 'blockers.field_outstanding')}
        </StatusPill>
      </div>
      <Switch>
        <Match when={vault(props.field)}>
          {(f) => (
            <>
              <p class="blocker-needs__detail">
                {t('blockers.needs_environment', { environment: f().environment })}
              </p>
              <Show when={f().description}>
                {(d) => <p class="blocker-needs__detail">{d()}</p>}
              </Show>
            </>
          )}
        </Match>
        <Match when={mcp(props.field)}>
          {(f) => (
            <>
              <p class="blocker-needs__detail">
                <Show when={f().url} fallback={t('blockers.field_mcp_url_absent')}>
                  {(url) => t('blockers.field_mcp_url', { url: url() })}
                </Show>
              </p>
              <Show when={f().transport}>
                {(tr) => (
                  <p class="blocker-needs__detail">
                    {t('blockers.field_mcp_transport', { transport: tr() })}
                  </p>
                )}
              </Show>
              <Show when={f().token_ref}>
                {(ref) => (
                  <p class="blocker-needs__detail">
                    {t('blockers.field_mcp_token_ref', { name: ref() })}
                  </p>
                )}
              </Show>
            </>
          )}
        </Match>
        <Match when={oauth(props.field)}>
          {(f) => (
            <>
              <Show when={f().scopes}>
                {(scopes) => (
                  <p class="blocker-needs__detail">
                    {t('blockers.field_oauth_scopes', { scopes: scopes().join(', ') })}
                  </p>
                )}
              </Show>
              <Show when={!props.landed}>
                <p class="blocker-needs__detail">{t('blockers.field_oauth_session')}</p>
                <a
                  class={`${buttonClass({ variant: 'outline', size: 'sm' })} blocker-needs__connect`}
                  href={integrationOauthLoginUrl(f().provider)}
                >
                  {t('blockers.field_oauth_connect', { provider: fieldTitle(f()) })}
                </a>
              </Show>
            </>
          )}
        </Match>
      </Switch>
    </li>
  );
}
